<template>
    <header>
        <nav class="w-full bg-[#54586a] py-2 shadow">
            <div class="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8">
                <div class="flex justify-between items-center h-16">
                    <router-link to="/" class="text-xl font-bold text-white">
                        <img
                            class="h-14"
                            src="/assets/img/logo.png" alt="">
                    </router-link>

                    <div class="hidden md:flex space-x-6">
                        <template v-for="(item, i) in navItems" :key="i">
                            <router-link
                                v-if="item.type === 'router'"
                                :to="item.to"
                                class="text-white hover:text-black font-medium transition"
                            >
                                {{ item.label }}
                            </router-link>

                            <a
                                v-else
                                :href="item.to"
                                class="text-white flex items-center hover:text-black font-medium transition"
                                :target="item.newTab ? '_blank' : null"
                                :rel="item.newTab ? 'noopener noreferrer' : null"
                            >
                                {{ item.label }}
                                <svg
                                    v-if="item.newTab"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                    class="size-5 ml-1"
                                >
                                    <path fill-rule="evenodd" d="M5.22 14.78a.75.75 0 0 0 1.06 0l7.22-7.22v5.69a.75.75 0 0 0 1.5 0v-7.5a.75.75 0 0 0-.75-.75h-7.5a.75.75 0 0 0 0 1.5h5.69l-7.22 7.22a.75.75 0 0 0 0 1.06Z" clip-rule="evenodd" />
                                </svg>
                            </a>
                        </template>
                    </div>

                    <button
                        @click="isOpen = !isOpen"
                        class="md:hidden focus:outline-none z-50"
                    >
                        <svg
                            v-if="!isOpen"
                            xmlns="http://www.w3.org/2000/svg"
                            class="h-6 w-6 text-white"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                  d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                        <svg
                            v-else
                            xmlns="http://www.w3.org/2000/svg"
                            class="h-6 w-6 text-black"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                  d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            </div>

            <transition name="slide">
                <div
                    v-if="isOpen"
                    class="fixed inset-0 bg-white flex flex-col items-center justify-center space-y-8 text-lg z-40"
                >
                    <template v-for="(item, i) in navItems" :key="i">
                        <router-link
                            v-if="item.type === 'router'"
                            :to="item.to"
                            class="text-black font-semibold"
                            @click="isOpen = false"
                        >
                            {{ item.label }}
                        </router-link>

                        <a
                            v-else
                            :href="item.to"
                            class="text-black font-semibold flex items-center"
                            :target="item.newTab ? '_blank' : null"
                            :rel="item.newTab ? 'noopener noreferrer' : null"
                            @click="isOpen = false"
                        >
                            {{ item.label }}
                            <svg
                                v-if="item.newTab"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                                class="size-6 ml-1"
                            >
                                <path fill-rule="evenodd" d="M5.22 14.78a.75.75 0 0 0 1.06 0l7.22-7.22v5.69a.75.75 0 0 0 1.5 0v-7.5a.75.75 0 0 0-.75-.75h-7.5a.75.75 0 0 0 0 1.5h5.69l-7.22 7.22a.75.75 0 0 0 0 1.06Z" clip-rule="evenodd" />
                            </svg>
                        </a>
                    </template>
                </div>
            </transition>
        </nav>
    </header>
</template>

<script setup>
import { ref } from 'vue';

const isOpen = ref(false);
const user = window.PerfectStorm?.user ?? null;

const navItems = [
    { label: 'Home', to: '/', type: 'router' },
    { label: 'Search', to: '/search', type: 'router' },
    { label: 'FAQs', to: '/faqs', type: 'router' },
    { label: 'How To: Pack/Ship', to: '/how-to-pack-ship', type: 'router' },
    { label: 'Collection Estimate', to: '/collection-estimate', type: 'router' },
    ...(user
        ? [
            {
                label: 'My Account',
                to: user.is_admin ? '/admin' : '/account',
                type: 'link',
            },
        ]
        : [
            { label: 'Sign Up', to: '/register', type: 'link' },
            { label: 'Login', to: '/login', type: 'link' },
        ]),
    { label: 'Terms', to: '/terms', type: 'router' },
    { label: 'Privacy Policy', to: '/privacy', type: 'router' },
    { label: 'About Us', to: 'https://perfectstormmtg.net/', type: 'link', newTab: true },
];
</script>

<style>
.slide-enter-active,
.slide-leave-active {
    transition: transform 0.3s ease;
}
.slide-enter-from {
    transform: translateX(100%);
}
.slide-enter-to {
    transform: translateX(0);
}
.slide-leave-from {
    transform: translateX(0);
}
.slide-leave-to {
    transform: translateX(100%);
}
</style>